import React, { useState } from "react";
import { Button, Form, Offcanvas, Badge } from "react-bootstrap";
import { 
    FaSearch, 
    FaSlidersH, 
    FaEllipsisV, 
    FaCog,
    FaTimes
} from "react-icons/fa";
import { FiUpload } from "react-icons/fi"; 
import { useTranslation } from "react-i18next";

const OrganizationToolbar = ({
    search,
    setSearch,
    filters,
    setFilters,
    delegations = [],
    teams = [],
    onAddPerson,
    canManageUsers = false,
    canViewDeleted = false,
}) => {
    const { t } = useTranslation(); 
    const [showSearch, setShowSearch] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const activeFiltersCount = 
        (filters.delegations?.length || 0) + 
        (filters.teams?.length || 0) + 
        (filters.startDate ? 1 : 0) +
        (filters.deletedOnly ? 1 : 0);

    const handleArrayFilter = (category, value, isChecked) => {
        setFilters((prev) => {
            const currentSelected = prev[category] || [];
            if (isChecked) {
                return { ...prev, [category]: [...currentSelected, value] };
            } else {
                return { ...prev, [category]: currentSelected.filter((item) => item !== value) };
            }
        });
    };

    const handleDateChange = (e) => {
        setFilters((prev) => ({ ...prev, startDate: e.target.value }));
    };

    const clearFilters = () => {
        setFilters({ delegations: [], teams: [], startDate: "", deletedOnly: false });
    };

    return (
        <div className="mb-3 mt-4">
            
            {canManageUsers && (
                <div className="d-flex flex-column gap-2 mb-4" style={{ maxWidth: "350px" }}>
                    <div className="atlar-card d-flex justify-content-between align-items-center p-3">
                        <span className="txt-subtle">{t("components.organizationToolbar.pendingInvites", "Pending invites")}</span>
                        <FaCog className="txt-subtle" style={{ cursor: "pointer" }} />
                    </div>
                    <div className="atlar-card d-flex justify-content-between align-items-center p-3">
                        <span className="txt-subtle">{t("components.organizationToolbar.notInvited", "Not invited")}</span>
                        <FaCog className="txt-subtle" style={{ cursor: "pointer" }} />
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-end align-items-center mb-3">
                
                <div className="d-flex gap-2 align-items-center">
                    
                    {showSearch || search ? (
                        <Form.Control
                            type="text"
                            placeholder={t("components.organizationToolbar.searchPlaceholder", "Search by name...")}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onBlur={() => { if (!search) setShowSearch(false) }}
                            autoFocus
                            className="bg-surface txt-main shadow-sm"
                            style={{ width: "250px", border: "var(--border-thin)" }}
                        />
                    ) : (
                        <Button 
                            variant="light" 
                            className="bg-surface txt-subtle shadow-sm position-relative"
                            style={{ border: "var(--border-thin)" }}
                            onClick={() => setShowSearch(true)}
                        >
                            <FaSearch />
                        </Button>
                    )}
                    
                    <Button 
                        variant="light" 
                        className="bg-surface txt-subtle shadow-sm position-relative" 
                        style={{ border: "var(--border-thin)" }}
                        onClick={() => setShowFilters(true)}
                    >
                        <FaSlidersH />
                        {activeFiltersCount > 0 && (
                            <Badge 
                                pill 
                                bg="danger" 
                                className="position-absolute top-0 start-100 translate-middle"
                                style={{ fontSize: "0.6rem" }}
                            >
                                {activeFiltersCount}
                            </Badge>
                        )}
                    </Button>
                    
                    {canManageUsers && (
                        <Button variant="light" className="bg-surface txt-subtle shadow-sm" style={{ border: "var(--border-thin)" }}>
                            <FaEllipsisV />
                        </Button>
                    )}

                    {canManageUsers && (
                        <Button variant="light" className="bg-surface txt-subtle fw-semibold d-flex align-items-center gap-2 shadow-sm" style={{ border: "var(--border-thin)" }}>
                            <FiUpload size={18} /> {t("components.organizationToolbar.exportData", "Export")}
                        </Button>
                    )}

                    {canManageUsers && (
                        <Button
                            className="border-0 shadow-sm fw-semibold px-3 py-2 d-flex align-items-center gap-2 txt-inverse"
                            style={{ backgroundColor: "var(--brand-primary)", borderRadius: "var(--btn-radius)" }}
                            onClick={onAddPerson}
                        >
                            + {t("components.organizationToolbar.addPerson", "Add Person")}
                        </Button>
                    )}
                    
                </div>
            </div>
 
            <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end" className="bg-surface">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="txt-main fw-bold">
                        {t("components.organizationToolbar.filtersTitle", "Advanced Filters")}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>

                    <Form.Group className="mb-4">
                        <Form.Label className="txt-subtle fw-semibold">{t("components.organizationToolbar.filterDelegation", "Delegation")}</Form.Label>
                        {delegations.map((del) => (
                            <Form.Check 
                                key={del.id}
                                type="checkbox"
                                id={`del-${del.id}`}
                                label={del.name}
                                className="txt-main mb-2"
                                checked={filters.delegations?.includes(del.name)}
                                onChange={(e) => handleArrayFilter("delegations", del.name, e.target.checked)}
                            />
                        ))}
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="txt-subtle fw-semibold">{t("components.organizationToolbar.filterTeams", "Teams")}</Form.Label>
                        {teams.map((team) => (
                            <Form.Check 
                                key={team.id}
                                type="checkbox"
                                id={`team-${team.id}`}
                                label={team.name}
                                className="txt-main mb-2"
                                checked={filters.teams?.includes(team.name)}
                                onChange={(e) => handleArrayFilter("teams", team.name, e.target.checked)}
                            />
                        ))}
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="txt-subtle fw-semibold">{t("components.organizationToolbar.filterStartDate", "Start Date (From)")}</Form.Label>
                        <Form.Control 
                            type="date" 
                            value={filters.startDate || ""}
                            onChange={handleDateChange}
                            className="bg-surface txt-main"
                        />
                    </Form.Group>

                    {canViewDeleted && (
                        <Form.Group className="mb-4">
                            <Form.Label className="txt-subtle fw-semibold">{t("components.organizationToolbar.filterStatus", "Status")}</Form.Label>
                            <Form.Check
                                type="switch"
                                id="filter-deleted-only"
                                label={t("components.organizationToolbar.deletedOnly", "Show only deleted users")}
                                className="txt-main"
                                checked={Boolean(filters.deletedOnly)}
                                onChange={(e) => {
                                    const deletedOnly = e.target.checked;
                                    setFilters((prev) => ({ ...prev, deletedOnly }));
                                }}
                            />
                        </Form.Group>
                    )}

                    <div className="d-flex gap-2 mt-5">
                        <Button 
                            variant="outline-secondary" 
                            className="w-50 fw-semibold" 
                            onClick={clearFilters}
                            disabled={activeFiltersCount === 0}
                        >
                            <FaTimes className="me-2"/> {t("common.clear", "Clear")}
                        </Button>
                        <Button 
                            className="w-50 fw-semibold txt-inverse border-0" 
                            style={{ backgroundColor: "var(--brand-primary)" }}
                            onClick={() => setShowFilters(false)}
                        >
                            {t("common.apply", "Apply")}
                        </Button>
                    </div>

                </Offcanvas.Body>
            </Offcanvas>

        </div>
    );
};

export default OrganizationToolbar;
